import { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { colorTokens } from '../../../../shared/styles/theme'
import { RADIUS } from '../../../../shared/styles/radii'
import { Note } from './Note'

/** back の上限と同じ(改善依頼・遠隔入力の一言とも 500)。400 を画面に出さない */
export const NOTE_MAX_LENGTH = 500

interface Props {
  open: boolean
  onClose: () => void
  /** 送れなかったら reject する。dialog は閉じず、書いた文章を残す */
  onSubmit: (note: string) => Promise<void>
  title: string
  placeholder: string
  confirmLabel: string
  /** 空でも送れる窓(遠隔入力の一言・申し送り)。既定は空では送れない(改善依頼) */
  allowEmpty?: boolean
}

/**
 * 文章 1 欄だけの dialog。説明文は置かない (log/63 D4)。改善依頼と、遠隔入力の
 * 一言(遠隔へ・断る)・申し送り(完了)が同じ形を使う。
 * 形は design-book `dialog.md`: 幅 sm・角丸 12・header 13/500 と hairline・✕ 常時可視・
 * DialogContent pb:1・暗幕は overlay.backdrop。
 *
 * 送れなかったときは閉じず、文章と「送れませんでした」を残す (閉じると文章の行き先が無い。
 * Kakeru君 の改善依頼と同じ)。閉じたら書きかけは捨てる (次の処方箋へ持ち越さない)。
 */
export const NoteDialog = ({
  open,
  onClose,
  onSubmit,
  title,
  placeholder,
  confirmLabel,
  allowEmpty = false,
}: Props) => {
  const [note, setNote] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [hasFailed, setHasFailed] = useState(false)

  const canSubmit = allowEmpty || !!note.trim()

  const handleClose = () => {
    if (isSending) return
    setNote('')
    setHasFailed(false)
    onClose()
  }

  const handleSubmit = async () => {
    const trimmed = note.trim()
    if (!canSubmit || isSending) return
    setIsSending(true)
    setHasFailed(false)
    try {
      await onSubmit(trimmed)
      setNote('')
      onClose()
    } catch {
      setHasFailed(true)
    } finally {
      setIsSending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            borderRadius: `${RADIUS.xl}px`,
            boxShadow: colorTokens.shadow.modal,
          },
        },
        backdrop: { sx: { backgroundColor: colorTokens.overlay.backdrop } },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${colorTokens.border.subtle}`,
          fontSize: 13,
          fontWeight: 500,
          color: colorTokens.fg.default,
        }}
      >
        {title}
        <IconButton
          size="small"
          aria-label="閉じる"
          onClick={handleClose}
          sx={{ ml: 'auto', color: colorTokens.fg.muted }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important', pb: 1 }}>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={4}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={placeholder}
          inputProps={{ maxLength: NOTE_MAX_LENGTH, 'aria-label': title }}
        />
        {hasFailed && <Note>送れませんでした</Note>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSending}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => void handleSubmit()}
          disabled={!canSubmit || isSending}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
